import React, { useEffect, useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useParams, useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../services/firebaseConfig";

import css from "./UserMeetingDetails.module.css";
import { Head } from "../../utils/Head";

export const UserMeetingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      navigate("/");
      return;
    }

    const docRef = doc(db, "meetings", id);
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setMeeting({ id: snapshot.id, ...data });
        } else {
          // reunião não encontrada — redireciona para página principal
          navigate("/");
        }
        setLoading(false);
      },
      (err) => {
        console.error("Erro no snapshot:", err);
        setError("Erro ao carregar os dados da reunião.");
        setLoading(false);
      }
    );

    return () => {
      unsubscribe && unsubscribe();
    };
  }, [id, navigate]);

  if (loading)
    return (
      <div className={css.container}>
        <p>Carregando...</p>
      </div>
    );
  if (error)
    return (
      <div className={css.container}>
        <p className={css.error}>{error}</p>
      </div>
    );
  if (!meeting)
    return (
      <div className={css.container}>
        <p>Reunião não encontrada.</p>
      </div>
    );

  // --- helpers ------------------------------------------------
  const scheduleDate = parseSchedule(meeting.scheduleFor);
  const formattedDate = scheduleDate
    ? scheduleDate.toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })
    : "—";
  const formattedTime = scheduleDate
    ? scheduleDate.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  const canJoin = meeting.moderatorOnline === true;

  function handleJoin() {
    if (!meeting?.jitsiRoomName) return;
    const url = `https://meet.jit.si/${meeting.jitsiRoomName}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  // format participants list safe
  const participants = Array.isArray(meeting.participants)
    ? meeting.participants
    : [];

  // ------------------------------------------------------------

  return (
    <div className={css.container}>
      <Head
        title={meeting.title || "Encontro"}
        description="Detalhes do encontro"
      />
      <div className={css.header}>
        <div>
          <h1 className={css.title}>{meeting.title}</h1>
          <p className={css.subtitle}>
            {formattedDate} • {formattedTime}
          </p>
          <p className={css.subtitleSmall}>
            Profissional: {meeting.proName || "—"}
          </p>
        </div>

        {canJoin && (
          <div className={css.actions}>
            <button
              className={css.joinBtn}
              onClick={handleJoin}
              disabled={!canJoin}
              aria-disabled={!canJoin}
              aria-live="polite"
            >
              Entrar na sala
            </button>
          </div>
        )}
      </div>

      <div role="status" className={css.statusRow} aria-live="polite">
        {meeting.moderatorOnline ? (
          <span className={css.badgeOnline}>Profissional online</span>
        ) : (
          <span className={css.badgeOffline}>Profissional offline</span>
        )}
      </div>

      {meeting.description && (
        <section className={css.info}>
          <h2>Descrição</h2>
          {meeting.description ? (
            <div className={css.description}>
              <ReactMarkdown>{meeting.description}</ReactMarkdown>
            </div>
          ) : (
            <p className={css.empty}>Sem descrição adicionada.</p>
          )}

          {/* <h3>Participantes</h3>
        {participants.length === 0 ? (
          <p className={css.empty}>Nenhum participante listado.</p>
        ) : (
          <ul className={css.participants}>
            {participants.map((p, idx) => (
              <li key={p?.id || p?.uid || `${idx}`}>
                <strong>{p?.name || p?.displayName || p}</strong>
                {p?.role && <span className={css.meta}> • {p.role}</span>}
              </li>
            ))}
          </ul>
        )} */}
        </section>
      )}
    </div>
  );
};

/* ---------------- helper ---------------- */

function parseSchedule(field) {
  if (!field) return null;
  // Firestore Timestamp
  if (typeof field === "object" && typeof field.toDate === "function") {
    return field.toDate();
  }
  // ISO string or number
  const d = new Date(field);
  if (!isNaN(d.getTime())) return d;
  return null;
}
